import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faLongArrowAltLeft, faLongArrowAltRight, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { refDebounced } from "@vueuse/core";
import { BButton, BFormInput } from "bootstrap-vue";
import { computed, nextTick, reactive, ref } from "vue";
import { useUid } from "@/composables/utils/uid";
import { useHighlight } from "./useHighlight";
import { filterOptions } from "./worker/filterOptions";
import { useSelectMany } from "./worker/selectMany";
export default /*#__PURE__*/ _defineComponent({
    __name: 'FormSelectMany',
    props: {
        id: { type: String, default: () => useUid("form-select-many-").value },
        disabled: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: "Search for options",
        },
        value: {
            type: Array,
            default: null,
        },
        maintainSelectionOrder: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["input"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faLongArrowAltLeft, faLongArrowAltRight, faTimes);
        const searchValue = ref("");
        const useRegex = ref(false);
        const caseSensitive = ref(false);
        const localSelectionOrder = computed(() => props.maintainSelectionOrder);
        const searchRegex = computed(() => {
            if (useRegex.value) {
                try {
                    const regex = new RegExp(searchValue.value, caseSensitive.value ? undefined : "i");
                    return regex;
                }
                catch (e) {
                    return null;
                }
            }
            else {
                return null;
            }
        });
        /** Wraps value prop so it can be set, and always returns an array */
        const selected = computed({
            get() {
                if (props.value === null) {
                    return [];
                }
                else if (Array.isArray(props.value)) {
                    return props.value;
                }
                else {
                    return [props.value];
                }
            },
            set(value) {
                emit("input", value);
            },
        });
        const regexInvalid = computed(() => useRegex.value && searchRegex.value === null);
        // Limits amount of displayed options
        const selectedDisplayCount = ref(1000);
        const unselectedDisplayCount = ref(1000);
        const filter = computed(() => searchRegex.value ?? searchValue.value);
        // binding to worker thread
        const { unselectedOptionsFiltered, selectedOptionsFiltered, running, moreUnselected, moreSelected } = useSelectMany({
            optionsArray: computed(() => props.options),
            filter,
            selected,
            selectedDisplayCount,
            unselectedDisplayCount,
            caseSensitive,
            maintainSelectionOrder: localSelectionOrder,
        });
        // debounced to it doesn't blink, and only appears when relevant
        const workerRunning = refDebounced(running, 400);
        /** generic event handler to handle highlighting of options */
        function handleHighlight(event, index, highlightHandler) {
            if (event.shiftKey && event.ctrlKey) {
                highlightHandler.rangeRemoveHighlight(index);
            }
            else if (event.shiftKey) {
                highlightHandler.rangeHighlight(index);
            }
            else if (event.ctrlKey) {
                highlightHandler.toggleHighlight(index);
            }
        }
        /** focus option at given index, or previous option if that doesn't exist */
        function focusOptionAtIndex(selected, index) {
            const el = document.getElementById(`${props.id}-${selected}-${index}`);
            if (el) {
                el.focus();
            }
            else {
                document.getElementById(`${props.id}-${selected}-${index - 1}`)?.focus();
            }
        }
        /** convert array of select options to a map of select labels to select values */
        function optionsToLabelMap(options) {
            return new Map(options.map((o) => [o.label, o.value]));
        }
        function valuesToOptions(values) {
            function stringifyObject(value) {
                return typeof value === "object" && value !== null ? JSON.stringify(value) : value;
            }
            const comparableValues = values.map(stringifyObject);
            const valueSet = new Set(comparableValues);
            const options = [];
            props.options.forEach((option) => {
                if (valueSet.has(stringifyObject(option.value))) {
                    options.push(option);
                }
            });
            return options;
        }
        async function selectOption(event, index) {
            if (event.shiftKey || event.ctrlKey) {
                handleHighlight(event, index, highlightUnselected);
            }
            else {
                const [option] = unselectedOptionsFiltered.value.splice(index, 1);
                if (option) {
                    selected.value = [...selected.value, option.value];
                }
                // select the element which now is where the removed element just was
                // to improve keyboard navigation
                await nextTick();
                focusOptionAtIndex("unselected", index);
            }
        }
        async function deselectOption(event, index) {
            if (event.shiftKey || event.ctrlKey) {
                handleHighlight(event, index, highlightSelected);
            }
            else {
                const [option] = selectedOptionsFiltered.value.splice(index, 1);
                if (option) {
                    const i = selected.value.findIndex((selectedValue) => {
                        if (typeof selectedValue === "string") {
                            return selectedValue === option.value;
                        }
                        else if (typeof selectedValue === "object" && typeof option.value === "object") {
                            // in case values are objects, compare their ids (if they have the 'id' property)
                            return selectedValue?.id === option.value?.id;
                        }
                        return false;
                    });
                    selected.value = selected.value.flatMap((value, index) => (index === i ? [] : [value]));
                }
                await nextTick();
                focusOptionAtIndex("selected", index);
            }
        }
        function selectAll() {
            if (highlightUnselected.highlightedIndexes.length > 0) {
                const highlightedValues = highlightUnselected.highlightedOptions.map((o) => o.value);
                selected.value = [...selected.value, ...highlightedValues];
                const highlightedMap = optionsToLabelMap(highlightUnselected.highlightedOptions);
                unselectedOptionsFiltered.value.filter((o) => highlightedMap.has(o.label));
            }
            else if (searchValue.value === "") {
                selected.value = props.options.map((o) => o.value);
                unselectedOptionsFiltered.value = [];
            }
            else {
                const filteredValues = filterOptions(props.options, filter.value, caseSensitive.value).map((o) => o.value);
                const selectedSet = new Set([...selected.value, ...filteredValues]);
                selected.value = Array.from(selectedSet);
                unselectedOptionsFiltered.value = [];
            }
        }
        function deselectAll() {
            if (highlightSelected.highlightedIndexes.length > 0) {
                const selectedMap = optionsToLabelMap(valuesToOptions(selected.value));
                const highlightedMap = optionsToLabelMap(highlightSelected.highlightedOptions);
                highlightedMap.forEach((_value, label) => selectedMap.delete(label));
                selected.value = Array.from(selectedMap.values());
                selectedOptionsFiltered.value.filter((o) => highlightedMap.has(o.label));
            }
            else if (searchValue.value === "") {
                selected.value = [];
                selectedOptionsFiltered.value = [];
            }
            else {
                const selectedSet = new Set(selected.value);
                const filteredValues = filterOptions(props.options, filter.value, caseSensitive.value).map((o) => o.value);
                filteredValues.forEach((v) => selectedSet.delete(v));
                selected.value = Array.from(selectedSet);
                selectedOptionsFiltered.value = [];
            }
        }
        function optionOnKey(selected, event, index) {
            // handle highlighting
            if ([" ", "Enter"].includes(event.key) && (event.shiftKey || event.ctrlKey)) {
                const highlightHandler = selected === "selected" ? highlightSelected : highlightUnselected;
                handleHighlight(event, index, highlightHandler);
                event.preventDefault();
                return;
            }
            if (!["ArrowUp", "ArrowDown"].includes(event.key)) {
                return;
            }
            event.preventDefault();
            // handle arrow navigation
            const nextIndex = event.key === "ArrowUp" ? index - 1 : index + 1;
            document.getElementById(`${props.id}-${selected}-${nextIndex}`)?.focus();
        }
        const highlightUnselected = reactive(useHighlight(unselectedOptionsFiltered));
        const highlightSelected = reactive(useHighlight(selectedOptionsFiltered));
        const selectText = computed(() => {
            if (highlightUnselected.highlightedIndexes.length > 0) {
                return "Select highlighted";
            }
            else if (searchValue.value === "") {
                return "Select all";
            }
            else {
                return "Select filtered";
            }
        });
        const deselectText = computed(() => {
            if (highlightSelected.highlightedIndexes.length > 0) {
                return "Deselect highlighted";
            }
            else if (searchValue.value === "") {
                return "Deselect all";
            }
            else {
                return "Deselect filtered";
            }
        });
        const unselectedCount = computed(() => {
            if (searchValue.value === "") {
                return `${props.options.length - selected.value.length}`;
            }
            else {
                let countString = `${unselectedOptionsFiltered.value.length}`;
                if (moreUnselected.value) {
                    countString += "+";
                }
                return countString;
            }
        });
        const selectedCount = computed(() => {
            if (searchValue.value === "") {
                return `${selected.value.length}`;
            }
            else {
                let countString = `${selectedOptionsFiltered.value.length}`;
                if (moreSelected.value) {
                    countString += "+";
                }
                return countString;
            }
        });
        return { __sfc: true, props, emit, searchValue, useRegex, caseSensitive, localSelectionOrder, searchRegex, selected, regexInvalid, selectedDisplayCount, unselectedDisplayCount, filter, unselectedOptionsFiltered, selectedOptionsFiltered, running, moreUnselected, moreSelected, workerRunning, handleHighlight, focusOptionAtIndex, optionsToLabelMap, valuesToOptions, selectOption, deselectOption, selectAll, deselectAll, optionOnKey, highlightUnselected, highlightSelected, selectText, deselectText, unselectedCount, selectedCount, FontAwesomeIcon, BButton, BFormInput };
    }
});
